import Cookies from 'js-cookie'
import planPermissionsMap from '@/utils/plans/planPermissionsMap'
import store from '@/store'
import VueRouter from 'vue-router'
import { EventBus } from '@/utils/eventBus'
import { privateRoutes, publicRoutes } from '@/router/app'
import { isSowingInCurrentSeasons } from '@/utils/seasonsCrops'
import { getUserPermissions } from '@/utils/firstLoadRequests'
import { notifySingleAlert } from '@/utils/alerts'

const PERMISSION_STATUS = {
  ALLOWED: 'ALLOWED',
  UPSELL: 'UPSELL',
  DISABLED: 'DISABLED',
}
const currentStore = store
const routes = [...privateRoutes, ...publicRoutes]
const router = new VueRouter({
  routes,
})

const isInCurrentSeasons = (to) => {
  const seasonsCrops = currentStore.getters['seasonsCrops/seasonsCrops']
  const currentSowingFromSemeaduras = currentStore.getters[
    'semeaduras/semeaduras'
  ].find((sowing) => sowing.id == to.query.semeadura_id)

  return isSowingInCurrentSeasons(currentSowingFromSemeaduras, seasonsCrops)
}

const checkPlansPermissions = (currentUser, path) => {
  if (currentUser) {
    const currentPlanPriority = currentUser.plan.priority_level

    if (planPermissionsMap[currentPlanPriority].allowedRoutes.includes(path)) {
      return PERMISSION_STATUS.ALLOWED
    } else if (
      planPermissionsMap[currentPlanPriority].upsellRoutes.includes(path)
    ) {
      return PERMISSION_STATUS.UPSELL
    } else if (
      planPermissionsMap[currentPlanPriority].disabledRoutes.includes(path)
    ) {
      return PERMISSION_STATUS.DISABLED
    }
  }

  return PERMISSION_STATUS.ALLOWED
}

const userAccessingViaUrlValidation = async () => {
  const storeIsReady = !!currentStore.getters['permissions/permissions'].length

  if (!storeIsReady) {
    await getUserPermissions()
  }
}

const hasPermissionToAccessRoute = async (to, next) => {
  const currentUser = currentStore.getters['user/currentUser']
  const isAdmin = currentUser?.is_admin

  const userPlanPermissionStatus = checkPlansPermissions(currentUser, to.path)
  if (userPlanPermissionStatus === PERMISSION_STATUS.UPSELL && isAdmin) {
    EventBus.$emit('plan-presentation-modal', to.name)
    return false
  } else if (userPlanPermissionStatus === PERMISSION_STATUS.DISABLED) {
    notifySingleAlert('exception.action_unauthorized')
    return false
  }

  const routeRequiredPermission = to.matched.find(
    (route) => to.path === route.path
  )?.meta.permission
  if (!routeRequiredPermission) return true

  await userAccessingViaUrlValidation(next)

  const isPlanFull = currentStore.getters['user/isPlanLevelFour']
  if (
    !!routeRequiredPermission &&
    !currentStore.getters['permissions/hasPermission'](routeRequiredPermission)
  ) {
    if (isPlanFull) {
      notifySingleAlert('plan.exception.no_permission')
      return false
    } else {
      notifySingleAlert('exception.action_unauthorized')
      return false
    }
  }

  return true
}

const myFarmPathRules = (from, to, next) => {
  if (to.path === '/my_farm' && !to.query.fazenda_id && from.query.fazenda_id) {
    // adding farm id
    next({
      path: '/my_farm',
      query: { fazenda_id: from.query.fazenda_id },
    })
    return false
  }
  return true
}

const paymentPathRules = (to) => {
  if (to.path === '/payment' && !currentStore.getters['user/isBrazil']) {
    notifySingleAlert('exception.action_unauthorized')
    return false
  }
  return true
}

const executionPathRules = (to) => {
  if (to.path === '/execucao') {
    if (!isInCurrentSeasons(to)) {
      notifySingleAlert('Home.sowing.not_current_season')
      return false
    }
  }
  return true
}

const originalPush = router.push
router.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }

  return originalPush.call(this, location).catch((err) => {
    if (err.name === 'NavigationDuplicated') {
      return Promise.resolve(this.currentRoute)
    } else {
      return Promise.reject(err)
    }
  })
}

const originalReplace = router.replace
router.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }

  return originalReplace.call(this, location).catch((err) => {
    if (err.name === 'NavigationDuplicated') {
      return Promise.resolve(this.currentRoute)
    } else {
      return Promise.reject(err)
    }
  })
}

router.beforeEach(async (to, from, next) => {
  // Get token from other apps, such as Admin Panel
  if (to.query.token) {
    Cookies.set('token', to.query.token, { expires: 1 })

    let query = Object.assign({}, to.query)
    delete query.token
    router.replace({ path: to.path, query })
  }

  if (to.path == '/account/activate') {
    next()
    return
  }

  if (!Cookies.get('token')) {
    if (to.path == '/login') {
      next()
      return
    }
    next('/login')
    return
  }

  // Check if route requires auth
  const routeRequiresAuth = to.matched.find((route) => {
    return to?.path == route?.path && route?.meta?.requiresAuth
  })
  if (!routeRequiresAuth && Cookies.get('token')) {
    if (!to.path.match('/reports/')) {
      next('/my_farm')
      return
    }
  }

  const permissionResult = await hasPermissionToAccessRoute(to, next)
  if (!permissionResult) return

  const myFarmPathResult = myFarmPathRules(from, to, next)
  if (!myFarmPathResult) return

  const paymentPathResult = paymentPathRules(to)
  if (!paymentPathResult) return

  const executionPathResult = executionPathRules(to)
  if (!executionPathResult) return

  next()
})

export { router }
export default VueRouter
